import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { OrderService } from '../../services/orders/order.service';
import { Order, ItemOrder } from '../../services/orders/interfaces/order.interface';
import { interval, Subscription, timer } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { OrderPreviewDialogComponent } from '../../dialogs/order-preview-dialog/order-preview-dialog.component';
import { MatProgressSpinner } from "@angular/material/progress-spinner";
import { ButtonComponent } from '../../ui/atoms/button/button.component';

interface KitchenItem extends ItemOrder {
  uuid: string;
  orderId: string;
  name: string;
  qty: number;
  description?: string;
  tableNumber?: string;
  timeElapsed: string;
  startTime: Date;
  itemStatusSlug?: string;
  tableName?: string;
  tableLabel?: string;
  customerName?: string;
  selectedOptionName?: string; // Selected option name for products with options
  isMarking?: boolean; // UI: loading state for 'Listo' button
  menuProductUuid?: string; // For grouping
  optionUuid?: string; // For grouping by option
}

// Grouped item that aggregates multiple items with same product/option
interface GroupedKitchenItem {
  name: string;
  selectedOptionName?: string;
  totalQty: number; // Sum of all quantities
  items: KitchenItem[]; // Original items for this group
  oldestTime: Date; // Oldest item time for display
  timeElapsed: string;
  isMarking?: boolean;
}

interface KitchenGroup {
  key: string;
  tableLabel: string;
  orderId: string;
  customerName?: string;
  since: Date;
  items: KitchenItem[];
  groupedItems: GroupedKitchenItem[]; // Aggregated items by product/option
  expanded: boolean; // Track collapse/expand state
  isFoodTruck: boolean; // Identify food truck orders
}

@Component({
  selector: 'app-kitchen',
  standalone: true,
  imports: [
    CommonModule,
    MatCardModule,
    MatIconModule,
    MatButtonModule,
    MatToolbarModule,
    MatTooltipModule,
    MatExpansionModule,
    MatDialogModule,
    MatProgressSpinner,
    ButtonComponent,
],
  templateUrl: './kitchen.component.html',
  styleUrls: ['./kitchen.component.scss']
})
export class KitchenComponent implements OnInit, OnDestroy {
  itemsInPreparation: KitchenItem[] = [];
  groups: KitchenGroup[] = [];
  isLoading = false;
  private refreshSubscription?: Subscription;
  private timerSubscription?: Subscription;
  private readonly REFRESH_INTERVAL = 30000; // 30 seconds

  constructor(private orderService: OrderService, private dialog: MatDialog) { }

  ngOnInit(): void {
    this.loadOrders();
    this.setupAutoRefresh();
  }

  ngOnDestroy(): void {
    this.refreshSubscription?.unsubscribe();
    this.timerSubscription?.unsubscribe();
  }

  statusInitial(item: KitchenItem): string {
    const raw: any = (item as any)?.itemStatusName || (item as any)?.itemStatusSlug || '';
    return String(raw).trim().charAt(0).toUpperCase();
  }

  openOrderPreview(grp: KitchenGroup): void {
    const map = new Map<string, { name: string; qty: number; options: { name: string; qty: number }[] }>();
    (grp.groupedItems || []).forEach(g => {
      const key = g.name || 'Producto';
      const entry = map.get(key) || { name: key, qty: 0, options: [] };
      entry.qty += g.totalQty || 0;
      const optName = (g.selectedOptionName || '').trim();
      if (optName) {
        const found = entry.options.find(o => o.name === optName);
        if (found) found.qty += g.totalQty || 0; else entry.options.push({ name: optName, qty: g.totalQty || 0 });
      }
      map.set(key, entry);
    });
    this.dialog.open(OrderPreviewDialogComponent, {
      width: '520px',
      data: {
        orderId: grp.orderId,
        tableLabel: grp.tableLabel,
        customerName: grp.customerName,
        since: grp.since,
        status: 'sent',
        groups: Array.from(map.values())
      }
    });
  }

  openOrderPreviewForItem(item: KitchenItem): void {
    const tableLabel = item.tableLabel || 'Sin mesa';
    const orderId = item.orderId || '';
    const customerName = item.customerName || '';
    const key = `${tableLabel}|${orderId}|${customerName}`;
    let grp = this.groups.find(g => g.key === key);
    if (!grp) {
      grp = this.groups.find(g => g.orderId === orderId);
    }
    if (!grp) return;
    this.openOrderPreview(grp);
  }

  private loadOrders(): void {
    this.isLoading = true;
    // Use the new endpoint to get sent items
    this.orderService.getSentOrderItems().subscribe({
      next: (items: any[]) => {
        this.processSentItems(items);
        this.isLoading = false;
      },
      error: (error: any) => {
        console.error('Error loading sent items:', error);
        this.isLoading = false;
      }
    });
  }

  private processSentItems(items: any[]): void {
    // Clear existing timers
    this.timerSubscription?.unsubscribe();

    // Process sent items
    this.itemsInPreparation = items.map(item => {
      // Extract option information from optionSnapshots or options array
      let selectedOptionName: string | undefined;
      let optionUuid: string | undefined;
      const optionSnapshots = item.optionSnapshots || item.options || [];

      if (Array.isArray(optionSnapshots) && optionSnapshots.length > 0) {
        // Get the first option's name (most common case is single option per item)
        const firstOption = optionSnapshots[0];
        selectedOptionName = firstOption.nameSnapshot || firstOption.name || undefined;
        // Use menuProductOptionUuid which is the same for all items with the same option
        optionUuid = firstOption.menuProductOptionUuid || firstOption.uuid || firstOption.optionUuid || undefined;

        // Debug logging
        console.log('[Kitchen Debug] Item:', item.menuProduct?.name, 'Option:', selectedOptionName, 'OptionUUID:', optionUuid, 'Full option:', firstOption);
      }

      // Prefer explicit clientName on the item, then fall back to order/client/table
      const customerName = (item.clientName
        || item.customerName
        || item.order?.clientName
        || item.order?.customerName
        || item.order?.table?.clientName
        || '').toString().trim();

      return {
        uuid: item.uuid || '',
        orderId: (item.order?.id !== undefined && item.order?.id !== null) ? String(item.order.id) : '',
        name: item.menuProduct?.name || 'Sin nombre',
        qty: item.qty || 1,
        price: item.price || 0,
        description: item.menuProduct?.description || '',
        tableNumber: item.order?.table?.name || undefined,
        tableLabel: item.order?.table?.name || (item.order?.tableUuid ? 'Mesa' : 'Sin mesa'),
        customerName,
        startTime: new Date(item.processedAt || new Date()),
        timeElapsed: '0s',
        itemStatusSlug: item.itemStatus?.slug || 'sent',
        itemStatusName: item.itemStatus?.name || 'Enviado',
        selectedOptionName,
        menuProduct: item.menuProduct || null,
        tableType: item.order?.table?.type || '', // Capture table type for food truck identification
        menuProductUuid: item.menuProduct?.uuid || item.menuProductUuid || '',
        optionUuid
      };
    });

    // Sort by time (oldest first)
    this.itemsInPreparation.sort((a, b) =>
      new Date(a.startTime).getTime() - new Date(b.startTime).getTime()
    );

    // Build groups for the current set
    this.buildGroups();

    // Start timer for all items
    this.startTimers();
  }

  private buildGroups(): void {
    const map = new Map<string, KitchenGroup>();
    this.itemsInPreparation.forEach(it => {
      const key = `${it.tableLabel || 'Sin mesa'}|${it.orderId || ''}|${it.customerName || ''}`;
      const existing = map.get(key);
      if (existing) {
        existing.items.push(it);
        if (new Date(it.startTime).getTime() < new Date(existing.since).getTime()) {
          existing.since = it.startTime;
        }
      } else {
        // Determine if this is a food truck order based on table type
        const tableType = (it as any).tableType || '';
        const isFoodTruck = tableType.toUpperCase() === 'FOODTRUCK';

        map.set(key, {
          key,
          tableLabel: it.tableLabel || 'Sin mesa',
          orderId: it.orderId || '',
          customerName: it.customerName || '',
          since: it.startTime,
          items: [it],
          groupedItems: [], // Will be populated below
          expanded: true, // Expanded by default
          isFoodTruck
        });
      }
    });

    // Now create groupedItems for each group
    map.forEach(group => {
      const itemMap = new Map<string, GroupedKitchenItem>();

      group.items.forEach(item => {
        // Create a unique key based on menuProductUuid and optionUuid
        const groupKey = `${item.menuProductUuid || 'no-product'}|${item.optionUuid || 'no-option'}`;

        const existing = itemMap.get(groupKey);
        if (existing) {
          existing.totalQty += item.qty;
          existing.items.push(item);
          // Update to oldest time
          if (new Date(item.startTime).getTime() < new Date(existing.oldestTime).getTime()) {
            existing.oldestTime = item.startTime;
          }
        } else {
          itemMap.set(groupKey, {
            name: item.name,
            selectedOptionName: item.selectedOptionName,
            totalQty: item.qty,
            items: [item],
            oldestTime: item.startTime,
            timeElapsed: item.timeElapsed,
            isMarking: false
          });
        }
      });

      group.groupedItems = Array.from(itemMap.values());
    });

    this.groups = Array.from(map.values()).sort((a, b) => a.since.getTime() - b.since.getTime());
  }

  private startTimers(): void {
    this.timerSubscription = interval(1000).subscribe(() => {
      this.itemsInPreparation.forEach(item => {
        item.timeElapsed = this.calculateElapsedTime(item.startTime);
      });
      // Also update grouped items
      this.groups.forEach(group => {
        group.groupedItems.forEach(groupedItem => {
          groupedItem.timeElapsed = this.calculateElapsedTime(groupedItem.oldestTime);
        });
      });
    });
  }

  private calculateElapsedTime(startTime: Date): string {
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - new Date(startTime).getTime()) / 1000);

    const hours = Math.floor(diffInSeconds / 3600);
    const minutes = Math.floor((diffInSeconds % 3600) / 60);
    const seconds = diffInSeconds % 60;

    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    } else {
      return `${seconds}s`;
    }
  }

  private setupAutoRefresh(): void {
    this.refreshSubscription = interval(this.REFRESH_INTERVAL).subscribe(() => {
      if (!this.isLoading) {
        this.loadOrders();
      }
    });
  }

  refreshOrders(): void {
    this.loadOrders();
  }

  isTimeWarning(timeString: string): boolean {
    // Check if time is more than 10 minutes (10:00)
    if (!timeString) return false;
    const [minutes, seconds] = timeString.split(':').map(Number);
    return minutes >= 10;
  }

  markAsReady(item: KitchenItem): void {
    if (!item.uuid || !item.orderId) return;

    // Call the service to update the item status to 'ready'
    item.isMarking = true;
    this.orderService.updateOrderItemStatus(item.orderId, item.uuid, 'ready').subscribe({
      next: () => {
        // Remove the item from the list on success
        this.itemsInPreparation = this.itemsInPreparation.filter(i => i.uuid !== item.uuid);
        // Rebuild groups to refresh the view immediately
        this.buildGroups();
        // Reload from backend to ensure full sync (timers, new items, etc.)
        this.loadOrders();
        item.isMarking = false;
      },
      error: (error) => {
        console.error('Error updating item status:', error);
        item.isMarking = false;
      }
    });
  }

  toggleGroup(group: KitchenGroup): void {
    group.expanded = !group.expanded;
  }

  markAsDelivered(item: KitchenItem): void {
    if (!item.uuid || !item.orderId) return;

    // Call the service to update the item status to 'delivered'
    item.isMarking = true;
    this.orderService.updateOrderItemStatus(item.orderId, item.uuid, 'delivered').subscribe({
      next: () => {
        // Remove the item from the list on success
        this.itemsInPreparation = this.itemsInPreparation.filter(i => i.uuid !== item.uuid);
        // Rebuild groups to refresh the view immediately
        this.buildGroups();
        // Reload from backend to ensure full sync (timers, new items, etc.)
        this.loadOrders();
        item.isMarking = false;
      },
      error: (error) => {
        console.error('Error updating item status to delivered:', error);
        item.isMarking = false;
      }
    });
  }

  // Mark all items in a grouped item as ready
  markGroupedAsReady(groupedItem: GroupedKitchenItem): void {
    if (!groupedItem.items || groupedItem.items.length === 0) return;

    groupedItem.isMarking = true;
    const updates = groupedItem.items.map(item =>
      this.orderService.updateOrderItemStatus(item.orderId, item.uuid, 'ready').toPromise()
    );

    Promise.all(updates).then(() => {
      // Remove all items from the list
      const uuidsToRemove = new Set(groupedItem.items.map(i => i.uuid));
      this.itemsInPreparation = this.itemsInPreparation.filter(i => !uuidsToRemove.has(i.uuid));
      this.buildGroups();
      this.loadOrders();
      groupedItem.isMarking = false;
    }).catch(error => {
      console.error('Error updating grouped items status:', error);
      groupedItem.isMarking = false;
    });
  }

  // Mark all items in a grouped item as delivered (for food truck)
  markGroupedAsDelivered(groupedItem: GroupedKitchenItem): void {
    if (!groupedItem.items || groupedItem.items.length === 0) return;

    groupedItem.isMarking = true;
    const updates = groupedItem.items.map(item =>
      this.orderService.updateOrderItemStatus(item.orderId, item.uuid, 'delivered').toPromise()
    );

    Promise.all(updates).then(() => {
      // Remove all items from the list
      const uuidsToRemove = new Set(groupedItem.items.map(i => i.uuid));
      this.itemsInPreparation = this.itemsInPreparation.filter(i => !uuidsToRemove.has(i.uuid));
      this.buildGroups();
      this.loadOrders();
      groupedItem.isMarking = false;
    }).catch(error => {
      console.error('Error updating grouped items status to delivered:', error);
      groupedItem.isMarking = false;
    });
  }
}
